/*
 * Tax display toggle
 * ------------------
 * Lets a storefront visitor switch displayed prices between
 *   - "incl"  : prices as Shopify renders them (include 25,5 % ALV)
 *   - "excl"  : the same prices with the VAT stripped out for reference
 *
 * This is a DISPLAY-ONLY helper. It never changes what the customer is
 * actually charged — cart, checkout and order totals are untouched.
 *
 * It works by reading the already-rendered money strings from the DOM,
 * so it adapts to whatever currency / money format the shop uses.
 * The choice is stored in localStorage and re-applied live (no reload),
 * including on AJAX price updates (variant change, quick add, paginated
 * collections, etc.) via a MutationObserver.
 */
(function () {
  'use strict';

  var cfg = window.taxToggleConfig || {};
  // VAT rate the displayed prices already INCLUDE (0.255 = 25,5 %).
  var RATE = typeof cfg.rate === 'number' ? cfg.rate : 0.255;
  var STORAGE_KEY = 'taxDisplayMode'; // 'incl' | 'excl'

  // Whitespace characters Shopify may use to group thousands.
  var NBSP = ' '; // no-break space
  var NNBSP = ' '; // narrow no-break space

  // Price elements we convert: product-display prices + cart line totals,
  // cart unit prices and the cart estimated total.
  var SELECTOR =
    '.price, .compare-at-price, .unit-price, ' +
    '.ww-product-card__price-current, .ww-product-card__price-compare, ' +
    '.cart-items__price, .cart-items__unit-price-value, .cart-totals__total-value';
  // Never touch prices inside these (price filters, the quick-order grand total,
  // and anything explicitly opted out — e.g. the invoice request dialog).
  var SKIP_CLOSEST = '.price-facet, .quick-order-list-total, [data-tax-skip]';

  // Matches a money number: integer part (optionally grouped by space, nbsp,
  // narrow-nbsp or dot) plus an optional ",dd" / ",d" decimal part.
  var TOKEN = new RegExp('\\d[\\d.\\u0020' + NBSP + NNBSP + ']*(?:,\\d{1,2})?', 'g');

  var observer = null;
  var rafId = null;

  function getMode() {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'excl' ? 'excl' : 'incl';
    } catch (e) {
      return 'incl';
    }
  }

  function setMode(mode) {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch (e) {
      /* private mode / storage disabled — still works for the session */
    }
  }

  function netValue(gross) {
    return gross / (1 + RATE);
  }

  // "1 234,56" / "12,75" / "1.234,56" -> Number
  function parseToken(token) {
    var cleaned = token.replace(/[^\d,]/g, '').replace(',', '.');
    return parseFloat(cleaned);
  }

  // Re-render a number in the same style as the token it replaces.
  function formatLike(token, value) {
    var groupChar = NBSP;
    if (token.indexOf(NBSP) > -1) groupChar = NBSP;
    else if (token.indexOf(NNBSP) > -1) groupChar = NNBSP;
    else if (token.indexOf(' ') > -1) groupChar = ' ';
    else if (/\.\d{3}/.test(token)) groupChar = '.';

    var hasDecimals = token.indexOf(',') > -1;
    var fixed = value.toFixed(hasDecimals ? 2 : 0);
    var parts = fixed.split('.');
    var intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, groupChar);
    return hasDecimals ? intPart + ',' + parts[1] : intPart;
  }

  function convertString(text) {
    TOKEN.lastIndex = 0;
    return text.replace(TOKEN, function (match) {
      var v = parseToken(match);
      if (!isFinite(v)) return match;
      return formatLike(match, netValue(v));
    });
  }

  // Build a VAT-stripped copy of the original HTML, touching text nodes only
  // so any inner markup (visually-hidden labels, etc.) is preserved.
  function buildConverted(originalHTML) {
    var tmp = document.createElement('span');
    tmp.innerHTML = originalHTML;
    var walker = document.createTreeWalker(tmp, NodeFilter.SHOW_TEXT, null, false);
    var node;
    while ((node = walker.nextNode())) {
      if (node.nodeValue && /\d/.test(node.nodeValue)) {
        node.nodeValue = convertString(node.nodeValue);
      }
    }
    return tmp.innerHTML;
  }

  function processElement(el, mode) {
    var current = el.innerHTML;
    // If the current markup isn't what we last wrote, the source price changed
    // underneath us — e.g. Shopify morphed the cart after a quantity update — so
    // re-capture it as the new gross original. Otherwise keep the stored one so
    // toggling back to "incl" restores the exact original (no rounding drift).
    if (el.getAttribute('data-tax-orig') === null || current !== el.getAttribute('data-tax-out')) {
      el.setAttribute('data-tax-orig', current);
    }
    var orig = el.getAttribute('data-tax-orig');
    var next = mode === 'excl' ? buildConverted(orig) : orig;
    if (el.innerHTML !== next) el.innerHTML = next;
    el.setAttribute('data-tax-out', next);
    // The `data-tax-applied` marker is what the anti-flash CSS keys off of:
    // unprocessed prices stay hidden in excl mode until we set it here.
    if (mode === 'excl') el.setAttribute('data-tax-applied', '');
    else el.removeAttribute('data-tax-applied');
  }

  function applyAll() {
    var mode = getMode();
    if (observer) observer.disconnect();

    // Keep <html> in sync so the anti-flash CSS hides/reveals correctly.
    document.documentElement.setAttribute('data-tax-mode', mode);

    var els = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.closest && el.closest(SKIP_CLOSEST)) continue;
      processElement(el, mode);
    }

    reflectButtons(mode);
    connectObserver();
  }

  function scheduleApply() {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(function () {
      rafId = null;
      applyAll();
    });
  }

  function connectObserver() {
    if (!('MutationObserver' in window)) return;
    if (!observer) {
      observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var m = mutations[i];
          if (!m) continue;
          // React to new nodes (AJAX/quick-add/pagination) and to in-place text
          // rewrites (Shopify morphs cart prices via characterData, not childList).
          if ((m.addedNodes && m.addedNodes.length) || m.type === 'characterData') {
            scheduleApply();
            return;
          }
        }
      });
    }
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  /* ---------------------------------------------------------------- UI --- */

  function reflectButtons(mode) {
    // The trigger label and active-option highlight are driven by the
    // html[data-tax-mode] attribute via CSS (set in <head> before first paint,
    // so there's no flicker when navigating). Here we only keep the ARIA state
    // and the .is-active class in sync for live (no-reload) changes.
    var btns = document.querySelectorAll('.tax-toggle__btn');
    for (var i = 0; i < btns.length; i++) {
      var active = btns[i].getAttribute('data-mode') === mode;
      btns[i].classList.toggle('is-active', active);
      btns[i].setAttribute('aria-selected', active ? 'true' : 'false');
    }
  }

  /** @param {Element|null} el */
  function closeDetails(el) {
    if (el) /** @type {HTMLDetailsElement} */ (el).open = false;
  }

  function init() {
    // One delegated listener handles every toggle — the header dropdown and the
    // mobile-drawer inline control — including any injected later via the Section
    // Rendering API, since we don't bind per-widget.
    document.addEventListener('click', function (e) {
      if (!(e.target instanceof Node)) return;
      var node = e.target;
      if (node instanceof Element) {
        var btn = node.closest('.tax-toggle__btn');
        if (btn) {
          setMode(btn.getAttribute('data-mode'));
          applyAll();
          // Only close our OWN dropdown — never an ancestor <details> such as the
          // mobile menu drawer (closing that directly desynced its open state and
          // left the hamburger stuck on the X icon).
          closeDetails(btn.closest('details.tax-toggle')); // null for the inline variant
          return;
        }
      }
      // Otherwise close any open dropdown when clicking outside it.
      document.querySelectorAll('details.tax-toggle[open]').forEach(function (el) {
        if (!el.contains(node)) closeDetails(el);
      });
    });
    applyAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
